/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Area } from '@src/app/model/area';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { Observable, of, TimeoutError } from 'rxjs';
import { MailMerge } from '@src/app/model/mailMerge';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { catchError, timeout } from 'rxjs/operators';

export interface MailMergeRecipient {
    channel: string;
    email: string;
    address: string;
    fullName: string;
    unitName: string;
    outstanding: string;
}

export interface MailMergeRecipientsSummary {
    units: number;
    emailContacts: number;
    letterContacts: number;
    totalContacts: number;
}

@Injectable({
    providedIn: 'root'
})
export class MailMergeService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);


    protected get baseUrl() {
        return this.getAjaxPath('mailmerge');
    }
    protected cache: Area[];
    protected typeString = 'MailMerge';

    linkRequest(mailMerge: MailMerge, sr: ServiceRequest): Observable<MailMerge> {

        const url = this.baseUrl + '/' + mailMerge.id + '/linkRequest';
        const object = {id: mailMerge.id, revision: mailMerge.revision, requestId: sr?.id}
        return this.http.put<MailMerge>(url, object, this.getSimpleOptions(mailMerge.teamId))
            .pipe(
                catchError(this.handleOneError('put' + this.typeString, null))
            );
    }

    preview(content: string, name: string, destination: string, params: object): Observable<{ html: string }> {
        const url = this.baseUrl + '/preview';
        return this.http.post<{ html: string }>(url, { content, name, destination, params }, this.getSimpleOptions(null)).pipe(
            catchError((error) => {
                this.messageService.showError(error, 'Previewing Mail Merge');
                return of(null);
            })
        );
    }

    sendTest(content: string, name: string, destination: string, params: object): Observable<{ status: string }> {
        const url = this.baseUrl + '/sendTest';
        return this.http.post<{ status: string }>(url, { content, name, destination, params }, this.getSimpleOptions(null)).pipe(
            catchError((error) => {
                this.messageService.showError(error, 'Sending Test Mail Merge');
                return of(null);
            })
        );
    }

    previewRecipients(content: string, name: string, destination: string, params: object): Observable<{ recipients: MailMergeRecipient[]; summary: MailMergeRecipientsSummary }> {
        const url = this.baseUrl + '/data';
        return this.http.post<{ recipients: MailMergeRecipient[]; summary: MailMergeRecipientsSummary }>(url, { content, name, destination, params }, this.getSimpleOptions(null)).pipe(
            timeout(90000),
            catchError((error) => {
                if (error instanceof TimeoutError) {
                    this.messageService.showErrorMessage('Very sorry, sometimes we cannot preview the data for large numbers of units.');
                } else {
                    this.messageService.showError(error, 'Previewing Mail Merge Recipients');
                }
                return of(null);
            })
        );
    }
}
